import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { buildStreamingWebrtcUrl, stripRtspUserinfo } from '../api/streaming/streaming.mapper';
import type {
  StreamingDisconnectRequest,
  StreamingInitializeRequest,
  StreamingInitializeResponse,
} from '../models/entities/streaming.model';
import { ApiRuntimeConfig } from './api-runtime-config';
import { AuthSessionService } from './auth-session.service';

@Injectable({ providedIn: 'root' })
export class StreamingService {
  private readonly http = inject(HttpClient);
  private readonly apiConfig = inject(ApiRuntimeConfig);
  private readonly auth = inject(AuthSessionService);
  private readonly initializedPaths = new Map<string, StreamingInitializeResponse>();
  private readonly pendingInitializations = new Map<
    string,
    Promise<StreamingInitializeResponse | null>
  >();
  /** WebRTC playback URL → initialize `path`, used by POST /streaming/disconnect. */
  private readonly initializedWebrtcPaths = new Map<string, string>();

  resolveWebrtcUrl(
    mediaMtxPath: string | null | undefined,
    startMs?: number,
    siteId?: number,
    playbackRate?: number,
  ): Promise<string | null> {
    const normalizedPath = mediaMtxPath?.trim();
    if (!normalizedPath) {
      return Promise.resolve(null);
    }

    const playbackStartMs = this.resolvePlaybackStartMs(startMs);
    const historicalRate = this.resolveHistoricalPlaybackRate(playbackStartMs, playbackRate);
    return this.resolveInitializeResponse(normalizedPath, playbackStartMs, siteId, historicalRate).then((response) => {
      if (!response) {
        return null;
      }
      return buildStreamingWebrtcUrl(response) ?? null;
    });
  }

  /**
   * Ends an initialized MediaMTX session. Failures are swallowed so camera
   * removal and component teardown can continue.
   */
  disconnectWebrtcUrl(webrtcUrl: string | null | undefined): Promise<void> {
    const url = webrtcUrl?.trim();
    if (!url) {
      return Promise.resolve();
    }

    const path = this.initializedWebrtcPaths.get(url);
    this.evictInitializedUrl(url);
    if (!path) {
      return Promise.resolve();
    }

    const request: StreamingDisconnectRequest = { path };
    const baseUrl = this.apiConfig.streamingInitializeBaseUrl().replace(/\/+$/, '');
    return firstValueFrom(this.http.post(`${baseUrl}/streaming/disconnect`, request))
      .then(() => undefined)
      .catch(() => undefined);
  }

  /** `null` means live — the initialize path carries no millisecond suffix. */
  private resolvePlaybackStartMs(startMs?: number): number | null {
    if (typeof startMs === 'number' && Number.isFinite(startMs) && startMs > 0) {
      return Math.trunc(startMs);
    }
    return null;
  }

  /** 1.5 and 2 only, and only once a historical start exists. */
  private resolveHistoricalPlaybackRate(
    playbackStartMs: number | null,
    playbackRate?: number,
  ): number | null {
    if (playbackStartMs === null || (playbackRate !== 1.5 && playbackRate !== 2)) {
      return null;
    }
    return playbackRate;
  }

  private cacheKey(
    normalizedPath: string,
    playbackStartMs: number | null,
    historicalRate: number | null,
  ): string {
    const startKey = `${normalizedPath}#${playbackStartMs ?? 'live'}`;
    return historicalRate === null ? startKey : `${startKey}#${historicalRate}`;
  }

  private streamingBaseUrl(): string {
    return this.apiConfig.streamingInitializeBaseUrl().replace(/\/+$/, '');
  }

  private resolveInitializeResponse(
    normalizedPath: string,
    playbackStartMs: number | null,
    siteId?: number,
    historicalRate: number | null = null,
  ): Promise<StreamingInitializeResponse | null> {
    const key = this.cacheKey(normalizedPath, playbackStartMs, historicalRate);
    const cached = this.initializedPaths.get(key);
    if (cached) {
      return Promise.resolve(cached);
    }

    const pending = this.pendingInitializations.get(key);
    if (pending) {
      return pending;
    }

    const basePath = stripRtspUserinfo(normalizedPath).replace(/\/+$/, '');
    const request: StreamingInitializeRequest = {
      mediaMtxPath: playbackStartMs === null ? basePath : `${basePath}/${playbackStartMs}`,
      siteId: typeof siteId === 'number' && Number.isFinite(siteId) ? siteId : 0,
      source: 'EventQ',
      userId: this.auth.userId(),
      userEmail: this.auth.email(),
    };
    if (historicalRate !== null) {
      request.playbackRate = historicalRate;
    }
    const initialization = firstValueFrom(
      this.http.post<StreamingInitializeResponse>(
        `${this.streamingBaseUrl()}/legacy/initialize`,
        request,
      ),
    )
      .then((response) => {
        const webrtcUrl = buildStreamingWebrtcUrl(response);
        if (webrtcUrl) {
          this.initializedPaths.set(key, response);
          this.initializedWebrtcPaths.set(webrtcUrl, response.path.trim().replace(/^\/+|\/+$/g, ''));
          return response;
        }
        return null;
      })
      .catch(() => null)
      .finally(() => {
        this.pendingInitializations.delete(key);
      });

    this.pendingInitializations.set(key, initialization);
    return initialization;
  }

  private evictInitializedUrl(webrtcUrl: string): void {
    this.initializedWebrtcPaths.delete(webrtcUrl);
    for (const [key, response] of this.initializedPaths.entries()) {
      if (buildStreamingWebrtcUrl(response) === webrtcUrl) {
        this.initializedPaths.delete(key);
      }
    }
  }
}
